import type { WidgetOptions, SettingsAdapter } from '../types/charting-library';
import type { TradingViewDatafeed } from '../types/datafeed-api';
import type { ChartLayout } from '../types/settings';
import { getCustomIndicators } from './customIndicators';

interface BuildWidgetOptionsInput {
  container: HTMLElement;
  datafeed: TradingViewDatafeed;
  symbol: string;
  interval: string;
  layout: ChartLayout | null;
  settingsAdapter: SettingsAdapter;
  layoutMode?: 'mobile' | 'desktop';
  enableCustomIndicators?: boolean;
}

export function buildWidgetOptions(input: BuildWidgetOptionsInput): WidgetOptions {
  const disabledFeatures = input.layoutMode === 'desktop'
    ? ['use_localstorage_for_settings']
    : ['use_localstorage_for_settings', 'left_toolbar', 'header_symbol_search', 'header_resolutions'];
  const enabledFeatures = ['chart_crosshair_menu'];

  const layout = input.layout ? applyMainSeriesState(input.layout, input.symbol, input.interval) : null;

  const options: WidgetOptions = {
    container: input.container,
    library_path: '/charting_library/',
    datafeed: input.datafeed,
    debug: false,
    symbol: input.symbol,
    interval: input.interval,
    locale: 'en',
    autosize: true,
    theme: 'dark',
    timezone: 'Etc/UTC',
    fullscreen: false,
    auto_save_delay: 5,
    saved_data: layout,
    custom_css_url: '/auth-iframe.css',
    disabled_features: disabledFeatures,
    enabled_features: enabledFeatures,
    settings_adapter: input.settingsAdapter,
    overrides: {
      'paneProperties.background': '#131722',
      'paneProperties.vertGridProperties.color': '#1e2a45',
      'paneProperties.horzGridProperties.color': '#1e2a45',
      'symbolWatermarkProperties.transparency': 90,
      'scalesProperties.textColor': '#888888',
      'mainSeriesProperties.showCountdown': true,
    },
  };

  if (input.enableCustomIndicators) {
    options.custom_indicators_getter = getCustomIndicators;
  }

  return options;
}

function applyMainSeriesState(layout: ChartLayout, symbol: string, interval: string): ChartLayout {
  return updateFirstMainSeries(layout, symbol, interval).value as ChartLayout;
}

function updateFirstMainSeries(value: unknown, symbol: string, interval: string): { value: unknown; updated: boolean } {
  if (!value || typeof value !== 'object') return { value, updated: false };

  if (Array.isArray(value)) {
    let updated = false;
    const nextItems = value.map((item) => {
      if (updated) return item;
      const next = updateFirstMainSeries(item, symbol, interval);
      updated = next.updated;
      return next.value;
    });
    return { value: updated ? nextItems : value, updated };
  }

  if ('type' in value && 'state' in value) {
    const source = value as { type?: unknown; state?: unknown };
    if (source.type === 'MainSeries' && source.state && typeof source.state === 'object') {
      return {
        value: {
          ...value,
          state: {
            ...source.state,
            symbol,
            interval,
          },
        },
        updated: true,
      };
    }
  }

  let updated = false;
  const entries = Object.entries(value).map(([key, child]) => {
    if (updated) return [key, child] as const;
    const next = updateFirstMainSeries(child, symbol, interval);
    updated = next.updated;
    return [key, next.value] as const;
  });

  return { value: updated ? Object.fromEntries(entries) : value, updated };
}
